import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  TemplatesAddTemplateSectionInput,
  TemplatesCreateTemplateInput,
  TemplatesDeleteTemplateSectionInput,
  TemplatesDiscardTemplateDraftInput,
  TemplatesExcludeTemplateBlocksInput,
  TemplatesInstantiateTemplateInput,
  TemplatesMergeTemplateSectionsInput,
  TemplatesPublishTemplateInput,
  TemplatesReadTemplateInput,
  TemplatesReinstateTemplateInput,
  TemplatesRestoreTemplateBlocksInput,
  TemplatesRetireTemplateInput,
  TemplatesSplitTemplateSectionInput,
  TemplatesUpdateTemplateInput,
  TemplatesUpdateTemplateSectionInput,
  TemplatesSetFieldValueInput,
  TemplatesAddTableRowInput,
  TemplatesAddEntryInput,
  TemplatesUpdateEntryInput,
  TemplatesRemoveEntryInput,
} from '@vivport/backend/types';
import { nanoid } from 'nanoid';
import * as templatesApi from '@/api/templates-api';
import { requireToolOutput } from '@/api/templates-api';
import { useAsyncStore, type AsyncTask } from '@/store/asyncStore';
import { contentKeys } from './useContentQueries';

export const templateKeys = {
  all: ['templates'] as const,
  list: () => [...templateKeys.all, 'list'] as const,
  reads: () => [...templateKeys.all, 'read'] as const,
  read: (ref: TemplatesReadTemplateInput) => [...templateKeys.reads(), ref] as const,
  instanceValues: (templateId: number) => [...templateKeys.all, 'instance-values', templateId] as const,
};

// Manual-bridge tool calls emit no stream events, so every template mutation invalidates its
// own read surfaces: the template read, the content detail, the document render family (the
// definition and instance pages render from it), the content-ref family (LayoutSidebar and
// route-level detail), and the content lists.
function invalidateTemplateSurfaces(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: templateKeys.all });
  void queryClient.invalidateQueries({ queryKey: contentKeys.details(), refetchType: 'all' });
  void queryClient.invalidateQueries({ queryKey: [...contentKeys.all, 'documentRender'] });
  void queryClient.invalidateQueries({
    predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
  });
  void queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
}

export const useTemplatesList = () =>
  useQuery({
    queryKey: templateKeys.list(),
    queryFn: async () => requireToolOutput(await templatesApi.listTemplates()),
    staleTime: 60 * 1000,
  });

export const useTemplateRead = (ref: TemplatesReadTemplateInput | null) =>
  useQuery({
    queryKey: ref === null ? [...templateKeys.reads(), 'disabled'] : templateKeys.read(ref),
    queryFn: async () => requireToolOutput(await templatesApi.readTemplate(ref as TemplatesReadTemplateInput)),
    enabled: ref !== null,
    staleTime: 30 * 1000,
  });

export const useTemplateInstanceValues = (templateId: number | null) =>
  useQuery({
    queryKey: templateId === null ? [...templateKeys.all, 'instance-values', 'disabled'] : templateKeys.instanceValues(templateId),
    queryFn: async () =>
      requireToolOutput(await templatesApi.readInstanceValues({ templateId: templateId as number, limit: 200 })),
    enabled: templateId !== null,
    staleTime: 30 * 1000,
  });

function useTemplateMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => invalidateTemplateSurfaces(queryClient),
  });
}

export const useCreateTemplate = () =>
  useTemplateMutation((input: TemplatesCreateTemplateInput) => templatesApi.createTemplate(input));

export const useUpdateTemplate = () =>
  useTemplateMutation((input: TemplatesUpdateTemplateInput) => templatesApi.updateTemplate(input));

export const useAddTemplateSection = () =>
  useTemplateMutation((input: TemplatesAddTemplateSectionInput) =>
    templatesApi.addTemplateSection(input)
  );

export const useUpdateTemplateSection = () =>
  useTemplateMutation((input: TemplatesUpdateTemplateSectionInput) =>
    templatesApi.updateTemplateSection(input)
  );

export const useMergeTemplateSections = () =>
  useTemplateMutation((input: TemplatesMergeTemplateSectionsInput) =>
    templatesApi.mergeTemplateSections(input)
  );

export const useSplitTemplateSection = () =>
  useTemplateMutation((input: TemplatesSplitTemplateSectionInput) =>
    templatesApi.splitTemplateSection(input)
  );

export const useExcludeTemplateBlocks = () =>
  useTemplateMutation((input: TemplatesExcludeTemplateBlocksInput) =>
    templatesApi.excludeTemplateBlocks(input)
  );

export const useRestoreTemplateBlocks = () =>
  useTemplateMutation((input: TemplatesRestoreTemplateBlocksInput) =>
    templatesApi.restoreTemplateBlocks(input)
  );

export const useDeleteTemplateSection = () =>
  useTemplateMutation((input: TemplatesDeleteTemplateSectionInput) =>
    templatesApi.deleteTemplateSection(input)
  );

export const usePublishTemplate = () =>
  useTemplateMutation((input: TemplatesPublishTemplateInput) =>
    templatesApi.publishTemplate(input)
  );

export const useRetireTemplate = () =>
  useTemplateMutation((input: TemplatesRetireTemplateInput) => templatesApi.retireTemplate(input));

export const useReinstateTemplate = () =>
  useTemplateMutation((input: TemplatesReinstateTemplateInput) =>
    templatesApi.reinstateTemplate(input)
  );

export const useDiscardTemplateDraft = () =>
  useTemplateMutation((input: TemplatesDiscardTemplateDraftInput) =>
    templatesApi.discardTemplateDraft(input)
  );

export const useInstantiateTemplate = () =>
  useTemplateMutation((input: TemplatesInstantiateTemplateInput) =>
    templatesApi.instantiateTemplate(input)
  );

export const useSetFieldValue = () =>
  useTemplateMutation((input: TemplatesSetFieldValueInput) => templatesApi.setFieldValue(input));

export const useAddTableRow = () =>
  useTemplateMutation((input: TemplatesAddTableRowInput) => templatesApi.addTableRow(input));

export const useAddEntry = () =>
  useTemplateMutation((input: TemplatesAddEntryInput) => templatesApi.addEntry(input));

export const useUpdateEntry = () =>
  useTemplateMutation((input: TemplatesUpdateEntryInput) => templatesApi.updateEntry(input));

export const useRemoveEntry = () =>
  useTemplateMutation((input: TemplatesRemoveEntryInput) => templatesApi.removeEntry(input));

export const TEMPLATE_WRITE_UP_TASK_PREFIX = 'template-write-up';

export function isWriteUpTaskRunningFor(tasks: AsyncTask[], instanceContentId: number): boolean {
  return tasks.some(
    (task) =>
      (task.status === 'pending' || task.status === 'processing') &&
      typeof task.contentId === 'string' &&
      task.contentId.startsWith(`${TEMPLATE_WRITE_UP_TASK_PREFIX}-${instanceContentId}-`)
  );
}

// The manual door for templates.writeUp: the same governed tool Neo calls, run as an AsyncQueue
// task under the member's ManualSession apex. The tool returns when the document is written, so
// the task settles from its result — no polling, no run state on the content payload.
export function useRunWriteUpTask() {
  const queryClient = useQueryClient();
  const addTask = useAsyncStore((state) => state.addTask);
  const updateTask = useAsyncStore((state) => state.updateTask);
  return async (instance: { instanceContentId: number; instanceTitle: string }) => {
    const taskId = addTask({
      title: `Write up · ${instance.instanceTitle}`,
      description: 'The write-up is filling the sections…',
      status: 'processing',
      progress: 0,
      contentType: 'DOCUMENT',
      contentId: `${TEMPLATE_WRITE_UP_TASK_PREFIX}-${instance.instanceContentId}-${nanoid(8)}`,
      payload: { remoteContentId: instance.instanceContentId },
    });
    try {
      const response = await templatesApi.writeUpTemplate({
        instanceContentId: instance.instanceContentId,
      });
      if (response.status === 'EXECUTED' && response.output?.completed === true) {
        updateTask(taskId, { status: 'completed', progress: 100, description: 'Write-up complete' });
      } else {
        updateTask(taskId, {
          status: 'failed',
          description: 'Write-up did not finish',
          error: response.output?.summary ?? response.error ?? 'Write-up failed',
        });
      }
    } catch (error) {
      updateTask(taskId, {
        status: 'failed',
        description: 'Write-up did not finish',
        error: error instanceof Error ? error.message : 'Write-up failed',
      });
    } finally {
      invalidateTemplateSurfaces(queryClient);
    }
  };
}
